import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { setCurrentUserRole } from '@/utils/userRole';
import AdminYardGraphPanel from '@/components/admin/AdminYardGraphPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Shield, Building2, Users, FolderKanban, Package, 
  Eye, UserCog, RefreshCw, Search, ArrowLeft,
  TrendingUp, Activity, BookMarked, Wrench
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  // DP53 — one class for the selected tab, so adding the Tools tab introduces no new
  // raw colour into this frozen file (the design-token guard forbids that).
  const activeTabClass = 'bg-[#70170F]';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const userId = localStorage.getItem('userId');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, companiesRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/admin/dashboard?user_id=${userId}`),
        axios.get(`${API}/api/admin/companies?user_id=${userId}`),
        axios.get(`${API}/api/admin/users?user_id=${userId}`)
      ]);
      
      setStats(statsRes.data);
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Super admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const response = await axios.post(`${API}/api/admin/impersonate`, {
        admin_user_id: userId,
        target_user_id: user.id
      });
      
      if (response.data.success) {
        // Store impersonation info
        localStorage.setItem('impersonating', 'true');
        localStorage.setItem('impersonationToken', response.data.impersonation_token);
        localStorage.setItem('originalUserId', userId);
        localStorage.setItem('originalUserName', localStorage.getItem('userName'));
        
        // Switch to impersonated user
        localStorage.setItem('userId', user.id);
        localStorage.setItem('userName', `${user.first_name} ${user.last_name}`);
        localStorage.setItem('userEmail', user.email);
        // Persist role through the canonical helper.
        setCurrentUserRole(user.role);
        
        if (response.data.company) {
          localStorage.setItem('companyId', response.data.company.id);
          localStorage.setItem('companyName', response.data.company.name);
        }
        
        toast.success(`Now viewing as ${user.first_name} ${user.last_name}`);
        setShowImpersonateDialog(false);
        navigate('/');
      }
    } catch (error) {
      console.error('Impersonation error:', error);
      toast.error('Failed to impersonate user');
    }
  };

  const handleStopImpersonating = () => {
    const originalUserId = localStorage.getItem('originalUserId');
    const originalUserName = localStorage.getItem('originalUserName');
    
    if (originalUserId) {
      localStorage.setItem('userId', originalUserId);
      localStorage.setItem('userName', originalUserName);
      localStorage.removeItem('impersonating');
      localStorage.removeItem('impersonationToken');
      localStorage.removeItem('originalUserId');
      localStorage.removeItem('originalUserName');
      
      toast.success('Returned to your account');
      window.location.reload();
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isImpersonating = localStorage.getItem('impersonating') === 'true';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#70170F] animate-spin mx-auto mb-2" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span>Viewing as: <strong>{localStorage.getItem('userName')}</strong> ({localStorage.getItem('companyName')})</span>
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleStopImpersonating}
            className="bg-white text-amber-600 hover:bg-amber-50"
          >
            Return to Admin
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to App
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#70170F]" />
                <h1 className="text-xl font-bold text-[#1A1A1A]">Super Admin</h1>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {/* Build 14.3 (2026-07-15): Rulebook + Diagnostics moved off the
                AdminSettings header and My Desk Admin-Tools block onto this
                Super Admin page. The routes themselves are unchanged —
                /admin/rulebook is still open to any admin (per 3b policy);
                /admin/diagnostics is still super-admin gated as before. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/rulebook')}
              data-testid="super-admin-rulebook-link"
              className="ml-2"
            >
              <BookMarked className="w-4 h-4 mr-2" />
              Rulebook
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/diagnostics')}
              data-testid="super-admin-diagnostics-link"
              className="ml-2 border-purple-300 text-purple-800 hover:bg-purple-50"
            >
              <Activity className="w-4 h-4 mr-2" />
              Diagnostics
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/import-bundle')}
              data-testid="open-import-bundle"
              className="ml-2 border-amber-300 text-amber-900 hover:bg-amber-50"
            >
              <Package className="w-4 h-4 mr-2" />
              One-Time Project Import
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_companies || 0}</p>
                  <p className="text-sm text-gray-500">Companies</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                  <p className="text-sm text-gray-500">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_projects || 0}</p>
                  <p className="text-sm text-gray-500">Projects</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_clips || 0}</p>
                  <p className="text-sm text-gray-500">Clips</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button 
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? activeTabClass : ''}
          >
            <Activity className="w-4 h-4 mr-2" />
            Overview
          </Button>
          <Button 
            variant={activeTab === 'companies' ? 'default' : 'outline'}
            onClick={() => setActiveTab('companies')}
            className={activeTab === 'companies' ? activeTabClass : ''}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Companies
          </Button>
          <Button 
            variant={activeTab === 'users' ? 'default' : 'outline'}
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? activeTabClass : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </Button>
          <Button
            variant={activeTab === 'tools' ? 'default' : 'outline'}
            onClick={() => setActiveTab('tools')}
            className={activeTab === 'tools' ? activeTabClass : ''}
            data-testid="super-admin-tools-tab"
          >
            <Wrench className="w-4 h-4 mr-2" />
            Tools
          </Button>
        </div>

        {/* Search */}
        {(activeTab === 'companies' || activeTab === 'users') && (
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'overview' && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Signups</CardTitle>
              <CardDescription>Newest companies on the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.recent_signups?.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>{company.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{company.plan || 'professional'}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-500">
                        {new Date(company.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {activeTab === 'companies' && (
          <Card>
            <CardHeader>
              <CardTitle>All Companies</CardTitle>
              <CardDescription>{filteredCompanies.length} companies</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Projects</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>{company.email}</TableCell>
                      <TableCell>{company.stats?.projects || 0}</TableCell>
                      <TableCell>{company.stats?.users || 0}</TableCell>
                      <TableCell>
                        <Badge variant={company.is_active ? 'default' : 'secondary'}>
                          {company.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedCompany(company)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* DP53 — MOVED here from Admin Settings > Branding. It repairs records in the
             live database, which is a Super Admin tool, not a look decision. Unchanged. */}
        {activeTab === 'tools' && (
          <div className="space-y-6" data-testid="super-admin-tools-panel">
            <AdminYardGraphPanel darkMode={false} cardClass="" textClass="" />
          </div>
        )}

        {activeTab === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle>All Users</CardTitle>
              <CardDescription>{filteredUsers.length} users across all companies</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.first_name} {user.last_name}
                        {user.is_super_admin && (
                          <Badge className="ml-2 bg-purple-600">Super Admin</Badge>
                        )}
                        {user.is_company_admin && (
                          <Badge variant="outline" className="ml-2">Admin</Badge>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.company_name || 'No company'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowImpersonateDialog(true);
                          }}
                          title="View as this user"
                        >
                          <UserCog className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Impersonate Dialog */}
      <Dialog open={showImpersonateDialog} onOpenChange={setShowImpersonateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>View as User</DialogTitle>
            <DialogDescription>
              You're about to view the app as <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> from <strong>{selectedUser?.company_name || 'No company'}</strong>.
              This allows you to see exactly what they see for troubleshooting.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p>A banner will appear at the top showing you're viewing as another user. Click "Return to Admin" to switch back.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImpersonateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => selectedUser && handleImpersonate(selectedUser)}
              className="bg-[#70170F] hover:bg-[#5a1209]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View as {selectedUser?.first_name}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
